// An embed playing in place over its link card (FigJam's embeds): a sandboxed iframe that follows
// the card as the canvas pans and zooms. Esc or clicking elsewhere stops it.
import { useEffect, useState } from 'react';
import { safeEmbed, type NodeId } from '@vellum/core';
import { app } from '../app.ts';
import { useApp } from '../hooks.ts';

/** The iframe for an embed URL, with only what players need. */
export function EmbedFrame({ src, title, style }: { src: string; title: string; style: React.CSSProperties }) {
  // only known players: the URL comes from the document, which any editor can set
  const safe = safeEmbed(src);
  if (!safe) return null;
  return <iframe src={safe} title={title} style={{ border: 0, borderRadius: 12, background: '#000', ...style }} allow="autoplay; encrypted-media; picture-in-picture; fullscreen; clipboard-write" allowFullScreen sandbox="allow-scripts allow-same-origin allow-popups allow-presentation allow-forms" referrerPolicy="strict-origin-when-cross-origin" />;
}

export function EmbedPlayer({ id }: { id: NodeId }) {
  useApp(['doc', 'ui'], () => `${app.docVersion}:${app.camera.x}:${app.camera.y}:${app.camera.zoom}`);
  const [, tick] = useState(0);
  useEffect(() => {
    const stop = (e: KeyboardEvent) => e.key === 'Escape' && app.setUI({ playingEmbedId: null });
    const off = app.onRender(() => tick((t) => t + 1));
    window.addEventListener('keydown', stop);
    // selecting something else (or nothing) stops the player
    const offSel = app.editor.on((e) => e.type === 'selection' && !e.selection.includes(id) && app.setUI({ playingEmbedId: null }));
    return () => {
      window.removeEventListener('keydown', stop);
      off();
      offSel();
    };
  }, [id]);
  const n = app.doc.get(id);
  if (!n?.linkPreview?.embed) return null;
  // the player covers the card's picture (its Player layer), else 16:9 at the top of the card
  const pic = app.doc.children(id).find((c) => c.name === 'Player');
  const b = app.doc.worldBounds(pic?.id ?? id);
  const a = app.pageToScreen({ x: b.x, y: b.y }),
    z = app.camera.zoom;
  const w = b.width * z,
    h = pic ? b.height * z : (w * 9) / 16;
  return (
    <div style={{ position: 'absolute', left: a.x, top: a.y, width: w, zIndex: 5 }} onPointerDown={(e) => e.stopPropagation()} onWheel={(e) => e.stopPropagation()}>
      <EmbedFrame src={n.linkPreview.embed} title={n.linkPreview.title ?? n.linkPreview.url} style={{ width: w, height: h, display: 'block' }} />
      <button className="btn secondary" style={{ position: 'absolute', top: -30, right: 0 }} onClick={() => app.setUI({ playingEmbedId: null })}>
        Done
      </button>
    </div>
  );
}
